import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Cloud, RefreshCw, CheckCircle, XCircle, Settings, Activity,
  Send, ChevronDown, ChevronUp, Eye, EyeOff, Inbox,
} from "lucide-react";
import { API } from "../config";
import { PasswordInput } from "../components/shared/PasswordInput";
import SalesforceInboundLogsWidget from "../components/admin/SalesforceInboundLogsWidget";

const SalesforcePage = () => {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Settings form
  const [form, setForm] = useState({
    login_url: "https://lojackmatrix--uatone.sandbox.my.salesforce.com",
    consumer_key: "",
    consumer_secret: "",
    username: "",
    password: "",
    security_token: "",
    api_version: "v60.0",
    object_type: "Lead",
    auth_flow: "client_credentials",
    customer_sync_path: "/services/apexrest/dealer/sync/",
    enabled: true,
  });

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [statusRes, settingsRes, logsRes] = await Promise.all([
        axios.get(`${API}/salesforce/status`),
        axios.get(`${API}/salesforce/settings`),
        axios.get(`${API}/salesforce/sync-logs?limit=20`),
      ]);
      setStatus(statusRes.data);
      setSettings(settingsRes.data);
      setLogs(logsRes.data.logs || []);
      // Pre-fill form with existing settings
      const s = settingsRes.data;
      setForm((prev) => ({
        ...prev,
        login_url: s.login_url || prev.login_url,
        username: s.username || "",
        api_version: s.api_version || "v60.0",
        object_type: s.object_type || "Lead",
        auth_flow: s.auth_flow || "client_credentials",
        customer_sync_path: s.customer_sync_path || prev.customer_sync_path,
        enabled: s.enabled ?? true,
      }));
    } catch {
      toast.error("Errore nel caricamento stato Salesforce");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/salesforce/test-connection`);
      toast.success(`Connesso a Salesforce: ${res.data.instance_url}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connessione Salesforce fallita");
    } finally {
      setTesting(false);
    }
  };

  const saveSettings = async () => {
    if (!form.consumer_key || (form.auth_flow === "password" && !form.username)) {
      toast.error(
        form.auth_flow === "password"
          ? "Consumer Key e Username sono obbligatori"
          : "Consumer Key obbligatoria"
      );
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/salesforce/settings`, form);
      toast.success("Impostazioni Salesforce salvate");
      fetchAll();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner"></div>
      </div>
    );
  }

  const configured = status?.configured;
  const stats = status?.stats || {};

  return (
    <div className="p-6 max-w-5xl mx-auto animate-fade-in" data-testid="salesforce-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Cloud className="text-blue-400" />
            Salesforce CRM
          </h1>
          <p className="text-gray-400 mt-1">Integrazione dati clienti e contratti</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchAll}
            className="btn-lojack-outline flex items-center gap-2 text-sm"
            data-testid="sf-refresh-btn"
          >
            <RefreshCw size={16} />
            Aggiorna
          </button>
          <button
            onClick={testConnection}
            disabled={testing || !configured}
            className="btn-lojack flex items-center gap-2 text-sm"
            data-testid="sf-test-btn"
          >
            {testing ? <div className="spinner-sm"></div> : <Send size={16} />}
            {testing ? "Test..." : "Test Connessione"}
          </button>
        </div>
      </div>

      {/* Status Banner */}
      <div
        className={`wallet-card mb-6 border ${
          configured ? "border-lojack-blue-600/30" : "border-lojack-clause-600/30"
        }`}
        data-testid="sf-status-banner"
      >
        <div className="flex items-center gap-4">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              configured ? "bg-lojack-blue-500/10" : "bg-lojack-clause-500/10"
            }`}
          >
            {configured ? (
              <CheckCircle className="text-lojack-blue-500" size={24} />
            ) : (
              <XCircle className="text-lojack-clause-500" size={24} />
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-white font-semibold">
              {configured ? "Salesforce Configurato" : "Salesforce Non Configurato"}
            </h3>
            <p className="text-gray-400 text-sm">
              {configured
                ? `Flusso: ${status.auth_flow === "password" ? "Username-Password" : "Client Credentials"} | Endpoint: ${status.customer_sync_path || "-"} | Sorgente: ${status.source}`
                : "Inserisci le credenziali della Connected App Salesforce per attivare la sincronizzazione"}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-800 pb-3">
        {[
          { key: "overview", label: "Panoramica", icon: Activity },
          { key: "settings", label: "Configurazione", icon: Settings },
          { key: "logs", label: "Log Sincronizzazione", icon: Cloud },
          { key: "inbound", label: "Dealer da Salesforce", icon: Inbox },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === t.key
                ? "bg-blue-600/20 text-blue-400 border border-blue-600/30"
                : "text-gray-400 hover:text-white hover:bg-gray-800"
            }`}
            data-testid={`sf-tab-${t.key}`}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              {
                label: "Sync Totali",
                value: stats.total_syncs || 0,
                color: "text-blue-400",
              },
              {
                label: "Completate",
                value: stats.successful || 0,
                color: "text-lojack-blue-400",
              },
              {
                label: "Fallite",
                value: stats.failed || 0,
                color: "text-red-400",
              },
              {
                label: "Ultima Sync",
                value: stats.last_successful_sync
                  ? new Date(stats.last_successful_sync).toLocaleString("it-IT")
                  : "Mai",
                color: "text-gray-400",
                isText: true,
              },
            ].map((s, i) => (
              <div
                key={i}
                className="wallet-card"
                data-testid={`sf-stat-${i}`}
              >
                <p className="text-gray-500 text-xs mb-1">{s.label}</p>
                <p className={`${s.color} ${s.isText ? "text-sm" : "text-2xl"} font-bold`}>
                  {s.value}
                </p>
              </div>
            ))}
          </div>

          {/* Field Mapping Info */}
          <div className="wallet-card">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Send size={16} className="text-blue-400" />
              Dati Inviati a Salesforce
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { local: "Ragione Sociale / Nome Cognome", sf: "company_name *" },
                { local: "Email", sf: "email" },
                { local: "Paese (ISO)", sf: "country" },
                { local: "Telefono (con prefisso)", sf: "phone" },
                { local: "Indirizzo + Civico", sf: "address" },
                { local: "Citta", sf: "city" },
                { local: "CAP", sf: "zip_code" },
                { local: "Nome", sf: "first_name" },
                { local: "Cognome", sf: "last_name" },
                { local: "P.IVA", sf: "vat_number" },
                { local: "Codice Fiscale", sf: "fiscal_code" },
              ].map((m, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-gray-800/50 rounded-lg px-4 py-2.5"
                >
                  <span className="text-gray-300 text-sm">{m.local}</span>
                  <span className="text-blue-400 text-xs font-mono">{m.sf}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-600 text-xs mt-3">
              Alla firma del contratto i dati vengono inviati all'endpoint Apex
              REST concordato con LoJack. La risposta restituisce il{" "}
              <span className="text-blue-400 font-mono">member_number</span>{" "}
              salvato sul cliente. Dati veicolo e PDF esclusi come da accordi
              (inseriti successivamente da ODS). Clienti privati: company_name
              vuoto (nome/cognome nei campi dedicati, classificati come Retail
              Account). Clienti business: company_name con ragione sociale + P.IVA.
            </p>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === "settings" && (
        <div className="wallet-card">
          <h3 className="text-white font-semibold mb-6 flex items-center gap-2">
            <Settings size={16} className="text-blue-400" />
            Credenziali Connected App Salesforce
          </h3>

          <div className="space-y-4">
            {/* Auth Flow */}
            <div>
              <label className="text-gray-400 text-xs">Flusso di Autenticazione OAuth 2.0</label>
              <select
                value={form.auth_flow}
                onChange={(e) =>
                  setForm((p) => ({ ...p, auth_flow: e.target.value }))
                }
                className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm"
                data-testid="sf-auth-flow"
              >
                <option value="client_credentials">
                  Client Credentials (consigliato — solo Key + Secret)
                </option>
                <option value="password">
                  Username-Password (legacy)
                </option>
              </select>
            </div>

            {/* Login URL */}
            <div>
              <label className="text-gray-400 text-xs">Login URL (My Domain)</label>
              <input
                value={form.login_url}
                onChange={(e) =>
                  setForm((p) => ({ ...p, login_url: e.target.value }))
                }
                placeholder="https://lojackmatrix--uatone.sandbox.my.salesforce.com"
                className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm font-mono"
                data-testid="sf-login-url"
              />
              <p className="text-gray-600 text-xs mt-1">
                UAT: lojackmatrix--uatone.sandbox.my.salesforce.com — in
                produzione andra sostituito con il My Domain di produzione.
              </p>
            </div>

            {/* Consumer Key + Secret */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-400 text-xs">Consumer Key *</label>
                <input
                  value={form.consumer_key}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, consumer_key: e.target.value }))
                  }
                  placeholder="3MVG9..."
                  className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm font-mono"
                  data-testid="sf-consumer-key"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs">
                  Consumer Secret *
                </label>
                <PasswordInput
                  value={form.consumer_secret}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, consumer_secret: e.target.value }))
                  }
                  placeholder="Inserisci Consumer Secret"
                  className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm font-mono"
                  data-testid="sf-consumer-secret"
                />
              </div>
            </div>

            {/* Username + Password (solo flusso legacy) */}
            {form.auth_flow === "password" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-400 text-xs">
                      Username Salesforce *
                    </label>
                    <input
                      value={form.username}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, username: e.target.value }))
                      }
                      placeholder="admin@company.com"
                      className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm"
                      data-testid="sf-username"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs">Password *</label>
                    <PasswordInput
                      value={form.password}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, password: e.target.value }))
                      }
                      placeholder="Inserisci Password"
                      className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm"
                      data-testid="sf-password"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-gray-400 text-xs">Security Token</label>
                  <PasswordInput
                    value={form.security_token}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, security_token: e.target.value }))
                    }
                    placeholder="Token ricevuto via email da Salesforce"
                    className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm font-mono"
                    data-testid="sf-security-token"
                  />
                  <p className="text-gray-600 text-xs mt-1">
                    Il Security Token viene inviato via email quando crei un utente
                    Salesforce. Va concatenato alla password nell'autenticazione.
                  </p>
                </div>
              </>
            )}

            {/* API Version + Endpoint Sync Clienti */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-400 text-xs">API Version</label>
                <input
                  value={form.api_version}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, api_version: e.target.value }))
                  }
                  placeholder="v60.0"
                  className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm"
                  data-testid="sf-api-version"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs">
                  Endpoint Sync Clienti (Apex REST)
                </label>
                <input
                  value={form.customer_sync_path}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, customer_sync_path: e.target.value }))
                  }
                  placeholder="/services/apexrest/dealer/sync/"
                  className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm font-mono"
                  data-testid="sf-customer-sync-path"
                />
              </div>
            </div>

            {/* Enable Toggle */}
            <div className="flex items-center justify-between bg-gray-800/50 rounded-xl p-4">
              <div>
                <p className="text-white font-medium">
                  Sincronizzazione Automatica
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  Invia automaticamente i dati a Salesforce alla firma del
                  contratto
                </p>
              </div>
              <button
                onClick={() =>
                  setForm((p) => ({ ...p, enabled: !p.enabled }))
                }
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${
                  form.enabled ? "bg-lojack-blue-600" : "bg-gray-700"
                }`}
                data-testid="sf-enabled-toggle"
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    form.enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Save */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={saveSettings}
                disabled={saving}
                className="btn-lojack flex items-center gap-2"
                data-testid="sf-save-btn"
              >
                {saving ? (
                  <div className="spinner-sm"></div>
                ) : (
                  <Settings size={16} />
                )}
                {saving ? "Salvataggio..." : "Salva Configurazione"}
              </button>
              <button
                onClick={testConnection}
                disabled={testing}
                className="btn-lojack-outline flex items-center gap-2"
                data-testid="sf-test-settings-btn"
              >
                {testing ? (
                  <div className="spinner-sm"></div>
                ) : (
                  <Send size={16} />
                )}
                {testing ? "Test..." : "Test Connessione"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === "logs" && (
        <div className="wallet-card">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Activity size={16} className="text-blue-400" />
            Storico Sincronizzazioni
          </h3>
          {logs.length > 0 ? (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {logs.map((log, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-gray-800/50 rounded-xl px-4 py-3"
                  data-testid={`sf-log-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        log.status === "success"
                          ? "bg-lojack-blue-500/10 text-lojack-blue-400"
                          : log.status === "failed"
                          ? "bg-red-500/10 text-red-400"
                          : "bg-lojack-clause-500/10 text-lojack-clause-400"
                      }`}
                    >
                      {log.status === "success" ? (
                        <CheckCircle size={14} />
                      ) : (
                        <XCircle size={14} />
                      )}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">
                        {log.customer_id?.substring(0, 8)}...
                        {(log.member_number || log.salesforce_record_id) && (
                          <span className="text-blue-400 text-xs ml-2">
                            SF: {log.member_number || log.salesforce_record_id}
                          </span>
                        )}
                      </p>
                      <p className="text-gray-500 text-xs">
                        {log.started_at &&
                          new Date(log.started_at).toLocaleString("it-IT")}
                        {log.sync_status &&
                          ` | ${log.sync_status === "duplicate" ? "Duplicato" : "Creato"}`}
                        {log.salesforce_object_type &&
                          ` | ${log.salesforce_object_type}`}
                        {log.pdf_attached && " | PDF allegato"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        log.status === "success"
                          ? "bg-lojack-blue-500/10 text-lojack-blue-400"
                          : "bg-red-500/10 text-red-400"
                      }`}
                    >
                      {log.status === "success" ? "OK" : "ERRORE"}
                    </span>
                    {log.error && (
                      <p className="text-red-400 text-xs mt-1 max-w-xs truncate">
                        {log.error}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Cloud size={40} className="mx-auto mb-3 text-gray-700" />
              <p className="text-gray-500">
                Nessuna sincronizzazione ancora eseguita
              </p>
              <p className="text-gray-600 text-xs mt-1">
                I dati verranno inviati automaticamente alla firma dei contratti
              </p>
            </div>
          )}
        </div>
      )}

      {/* Inbound (Dealer da Salesforce) Tab */}
      {activeTab === "inbound" && (
        <div className="bg-[#111] border border-gray-800 rounded-xl p-6">
          <SalesforceInboundLogsWidget />
        </div>
      )}
    </div>
  );
};

export default SalesforcePage;
